import { Headline3, theme } from 'aimo-ui';
import React, { useEffect } from 'react';
import {
  SafeAreaView,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import {
  SafeAreaViewProps,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import styled from 'styled-components/native';
import { StyledWrapper } from 'styles/ContainerStyles';
import { testIdentifiers } from 'utils/testIdentifiers';

export type ExtraPadding = 'all' | 'vertical' | 'horizontal' | 'top' | 'bottom';
export type AlignItems = 'center' | 'flex-start';
export type JustifyContent = 'center' | 'flex-start' | 'space-between';
const DEFAULT_PADDING = 20;

const TextWrapper = styled(StyledWrapper)`
  flex-shrink: 1;
`;

const RightWrapper = styled.View`
  padding-left: 20px;
`;

const LeftWrapper = styled.View`
  padding-right: 10px;
`;

export type SafeAreaHeaderContainerProps = {
  headline?: string | React.ReactNode;
  subtitle?: string;
  extraPadding?: ExtraPadding | ExtraPadding[];
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  extraPaddingValue?: number | { [key in ExtraPadding]?: number };
  leftAlignHeader?: boolean;
  headerContainerStyle?: StyleProp<ViewStyle>;
  useInsetForTop?: boolean;
  animationDuration?: number;
} & SafeAreaViewProps;

const SafeAreaHeaderContainer: React.FC<SafeAreaHeaderContainerProps> = ({
  headline,
  subtitle,
  leftContent,
  rightContent,
  extraPadding,
  extraPaddingValue = DEFAULT_PADDING,
  children,
  leftAlignHeader = false,
  headerContainerStyle,
  useInsetForTop = false,
  animationDuration = 0,
  ...props
}) => {
  const fillContentSize = useSharedValue(58);
  const animatedTopPadding = useSharedValue(0);
  const insets = useSafeAreaInsets();

  const isHeadlineString = typeof headline === 'string';
  const headlineLength = isHeadlineString ? headline.length : 0;

  useEffect(() => {
    animatedTopPadding.value = withTiming(useInsetForTop ? insets.top : 0, {
      duration: animationDuration,
      easing: Easing.out(Easing.cubic),
    });
  }, [useInsetForTop, insets.top, animationDuration, animatedTopPadding]);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      marginRight:
        fillContentSize.value - Math.max(0, headlineLength - 20) * 2.5,
    };
  }, [fillContentSize]);

  const animatedTopPaddingStyle = useAnimatedStyle(() => {
    return {
      paddingTop: animatedTopPadding.value,
    };
  });

  const staticTopPadding = { paddingTop: useInsetForTop ? insets.top : 0 };

  const AreaView = useInsetForTop ? View : SafeAreaView;

  return (
    <AreaView
      {...props}
      style={[
        styles(
          extraPaddingValue,
          extraPadding,
          !!headline,
          !!rightContent,
          !!leftContent
        ).safeView,
        props.style,
      ]}>
      {/* Animated padding sometimes gets lost and the header slides under the
          status bar, so only animate when we actually need to */}
      <Animated.View
        style={animationDuration ? animatedTopPaddingStyle : staticTopPadding}
      />
      <View
        style={[
          styles(
            extraPaddingValue,
            extraPadding,
            !!headline,
            !!rightContent,
            !!leftContent
          ).container,
          headerContainerStyle,
        ]}>
        {!leftAlignHeader && rightContent && !leftContent && !subtitle && (
          <Animated.View style={animatedStyle} />
        )}
        {leftContent && (
          <LeftWrapper
            onLayout={(e) =>
              (fillContentSize.value = e.nativeEvent.layout.width)
            }>
            {leftContent}
          </LeftWrapper>
        )}
        {isHeadlineString ? (
          <TextWrapper center mb={0}>
            <Headline3 numberOfLines={2} {...testIdentifiers(headline)} center>
              {headline}
            </Headline3>
          </TextWrapper>
        ) : (
          headline
        )}
        {children}
        {!!subtitle && subtitle?.length > 0 && (
          <Headline3
            {...testIdentifiers(subtitle)}
            color={theme.colors.gray300}>
            {subtitle}
          </Headline3>
        )}
        {!leftAlignHeader && !rightContent && leftContent && (
          <Animated.View style={animatedStyle} />
        )}
        {rightContent && (
          <RightWrapper
            onLayout={(e) =>
              (fillContentSize.value = e.nativeEvent.layout.width)
            }>
            {rightContent}
          </RightWrapper>
        )}
      </View>
    </AreaView>
  );
};

export default SafeAreaHeaderContainer;

const shouldApplyPadding = (
  extraPaddingValue: number | { [key in ExtraPadding]?: number },
  extraPadding: ExtraPadding | ExtraPadding[] | undefined
) => {
  if (!extraPadding) {
    return {};
  }

  const paddingStyles: { [key: string]: number } = {};

  const getPaddingValue = (paddingType: ExtraPadding) => {
    if (typeof extraPaddingValue === 'number') {
      return extraPaddingValue;
    } else {
      return extraPaddingValue[paddingType] ?? DEFAULT_PADDING;
    }
  };

  const applyPadding = (paddingType: ExtraPadding) => {
    switch (paddingType) {
      case 'top':
        paddingStyles.paddingTop = getPaddingValue('top');
        break;
      case 'bottom':
        paddingStyles.paddingBottom = getPaddingValue('bottom');
        break;
      case 'vertical':
        paddingStyles.paddingTop = getPaddingValue('vertical');
        paddingStyles.paddingBottom = getPaddingValue('vertical');
        break;
      case 'horizontal':
        paddingStyles.paddingLeft = getPaddingValue('horizontal');
        paddingStyles.paddingRight = getPaddingValue('horizontal');
        break;
      case 'all':
        paddingStyles.paddingTop = getPaddingValue('all');
        paddingStyles.paddingBottom = getPaddingValue('all');
        paddingStyles.paddingLeft = getPaddingValue('all');
        paddingStyles.paddingRight = getPaddingValue('all');
        break;
      default:
        break;
    }
  };

  if (Array.isArray(extraPadding)) {
    extraPadding.forEach((paddingType) => applyPadding(paddingType));
  } else {
    applyPadding(extraPadding);
  }

  return paddingStyles;
};

const contentJustify = (
  headline: boolean,
  rightContent: boolean,
  leftContent: boolean
) => {
  const onlyLeftContent = !headline && !rightContent && leftContent;
  return rightContent || onlyLeftContent ? 'space-between' : 'center';
};

const styles = (
  extraPaddingValue: number | { [key in ExtraPadding]?: number },
  extraPadding: ExtraPadding | ExtraPadding[] | undefined,
  headline: boolean,
  rightContent: boolean,
  leftContent: boolean
) => {
  return StyleSheet.create({
    safeView: { justifyContent: 'center' },
    container: {
      ...shouldApplyPadding(extraPaddingValue, extraPadding),
      flexDirection: 'row',
      justifyContent: contentJustify(headline, rightContent, leftContent),
      flexGrow: 1,
      alignItems: 'center',
    },
  });
};
